import { baseApi } from '@api/api';
import { resetAppState } from '@appState';
import { type AuthState, type JwtContent } from '@models/authState';
import { Role } from '@models/role';
import { User } from '@models/user';
import monitoring from '@monitoring';
import { handleError } from '@util/errors';
import service from '@util/service';
import { allResources, calcDuration, createCache, createStore, persist } from 'cross-state';
import { jwtDecode } from 'jwt-decode';
import { HTTPError } from 'ky';
import { z } from 'zod';

const authStore = createStore<{ refreshToken?: string }>({});

const { initialized } = persist(authStore, {
  id: 'auth-store',
  storage: localStorage,
});

const AuthResponse = z.object({ accessToken: z.string(), refreshToken: z.string() });

const authApi = service(
  'authApi',
  class AuthApi {
    auth = createCache(
      async () => {
        await initialized;
        const { refreshToken } = authStore.get();

        if (!refreshToken) {
          return null;
        }

        let res;

        try {
          res = await baseApi
            .post(`v1/login/refresh`, {
              json: { refreshToken },
              retry: { limit: 3, delay: () => 10_000, methods: ['post'] },
            })
            .json();
        } catch (error) {
          if (error instanceof HTTPError && [401, 403].includes(error.response.status)) {
            handleError(new Error('Session expired'));
            this.logout();
            return null;
          }

          throw error;
        }

        const { accessToken, refreshToken: newRefreshToken } = AuthResponse.parse(res);

        authStore.set('refreshToken', newRefreshToken);
        const decodedToken = jwtDecode<JwtContent>(accessToken);

        return {
          accessToken,
          decodedToken,
        } satisfies AuthState;
      },
      {
        invalidateAfter({ value }) {
          if (!value?.decodedToken.exp) {
            return null;
          }

          return value.decodedToken.exp * 1000 - Date.now() - calcDuration({ minutes: 1 });
        },
      },
    );

    me = createCache<{ user: User; roles: Role[] } | null>(() => async ({ use }) => {
      const auth = await use(this.auth);
      if (!auth) {
        return null;
      }

      const userRes = await baseApi
        .get(`v1/users/me`, {
          headers: { Authorization: `Bearer ${auth.accessToken}` },
        })
        .json();
      return z.object({ user: User, roles: Role.array() }).parse(userRes);
    });

    async login(userName: string, password: string) {
      const res = await baseApi.post(`v1/login`, { json: { userName, password } }).json();

      const { refreshToken } = AuthResponse.parse(res);
      authStore.set('refreshToken', refreshToken);
      this.resetApp();
    }

    logout() {
      authStore.set({});
      this.resetApp();
    }

    async verify(token: string, password?: string) {
      const response = await baseApi.post(`v1/login/verify`, { json: { token, password } }).json();
      const { refreshToken } = AuthResponse.parse(response);
      authStore.set('refreshToken', refreshToken);
      this.resetApp();
    }

    async forgotPassword(userName: string) {
      await baseApi.get(`v1/login/resetpw/${encodeURIComponent(userName)}`).json();
    }

    async resetPassword(token: string, password: string) {
      const response = await baseApi.post(`v1/login/resetpw`, { json: { token, password } }).json();
      const { refreshToken } = AuthResponse.parse(response);
      authStore.set('refreshToken', refreshToken);
      this.resetApp();
    }

    private resetApp() {
      allResources.clearAll();
      resetAppState();
    }
  },
);

export default authApi;

authApi.me.state.map('value').subscribe((me) => {
  if (!me) {
    monitoring?.api.resetUser();
  } else {
    monitoring?.api.setUser({
      username: me.user.userName,
      attributes: {
        roles: me.user.orgRoles.map((x) => `${x.orgId}:${x.roleId}`).join(','),
      },
    });
  }
});
